const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4001';

export type Tier = 'preview' | 'trial' | 'active' | 'blocked';

export interface PreviewMode {
  lag_seconds: number;
  symbols_limit: number;
  rate_hz: number;
}

export interface AccessResponse {
  tier: Tier;
  server_now: string;
  trial_expires_at: string | null;
  current_period_end: string | null;
  preview_expires_at: string | null;
  preview_mode: PreviewMode | null;
  blocked_reason?: 'new' | 'preview_expired' | null;
}

export interface PreviewResponse {
  tier: 'preview';
  server_now: string;
  preview_expires_at: string;
  preview_mode: PreviewMode;
}

export interface WsTokenResponse {
  token: string;
  server_now: string;
}

export interface VerifyResponse {
  tier: Tier;
  server_now: string;
  trial_expires_at: string | null;
  subscription_status: 'none' | 'active' | 'past_due' | 'canceled';
}

export interface CheckoutResponse {
  url: string;
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function getAccess(): Promise<AccessResponse> {
  const res = await fetch(`${API_URL}/access`, {
    credentials: 'include',
  });
  return handleResponse<AccessResponse>(res);
}

export async function startPreview(): Promise<PreviewResponse> {
  const res = await fetch(`${API_URL}/preview/start`, {
    method: 'POST',
    credentials: 'include',
  });
  return handleResponse<PreviewResponse>(res);
}

export async function getWsToken(): Promise<WsTokenResponse | null> {
  const res = await fetch(`${API_URL}/access/ws-token`, {
    credentials: 'include',
  });

  if (res.status === 401) {
    return null;
  }

  return handleResponse<WsTokenResponse>(res);
}

export async function requestMagicLink(email: string): Promise<{ ok: boolean }> {
  const res = await fetch(`${API_URL}/auth/magic-link/request`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  return handleResponse<{ ok: boolean }>(res);
}

export async function verifyMagicLink(email: string, token: string): Promise<VerifyResponse> {
  const res = await fetch(`${API_URL}/auth/magic-link/verify`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, token }),
  });
  return handleResponse<VerifyResponse>(res);
}

export async function createCheckout(): Promise<CheckoutResponse> {
  const res = await fetch(`${API_URL}/billing/checkout`, {
    method: 'POST',
    credentials: 'include',
  });
  return handleResponse<CheckoutResponse>(res);
}

export async function getBillingPortal(): Promise<{ url: string }> {
  const res = await fetch(`${API_URL}/billing/portal`, {
    credentials: 'include',
  });
  return handleResponse<{ url: string }>(res);
}

export async function logout(): Promise<void> {
  await fetch(`${API_URL}/auth/logout`, {
    method: 'POST',
    credentials: 'include',
  });
}

export function getWsUrl(): string {
  return API_URL.replace(/^http/, 'ws');
}
